import { CheckIcon } from "@radix-ui/react-icons";
import { PublicFormsService, PublicFormView } from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import PaymentStep, { PaymentHandle } from "components/Forms/PaymentStep";
import PublicForm, { PublicFormAnswers } from "components/Forms/PublicForm";
import { ReactNode, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

function Card({ children }: { children: ReactNode }) {
  return (
    // The app pads every page for its navbar; this page has none.
    <div className="-mt-[85px] min-h-screen bg-gray-50 px-4 py-8 sm:py-12">
      <div className="mx-auto w-full max-w-[560px] rounded-2xl bg-white p-6 shadow-sm sm:p-8">
        {children}
      </div>
    </div>
  );
}

function Message({
  title,
  text,
  done = false,
}: {
  title: string;
  text?: string;
  done?: boolean;
}) {
  return (
    <Card>
      <div className="py-8 text-center">
        {done && (
          <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-500 text-white">
            <CheckIcon width={28} height={28} />
          </div>
        )}
        <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
        {text && <p className="mt-3 text-base text-gray-600">{text}</p>}
      </div>
    </Card>
  );
}

/** The page a parent, student or tutor opens from the link they were sent. No login. */
export default function PublicFormPage() {
  const { token } = useParams();
  const [view, setView] = useState<PublicFormView>();
  const [loadFailed, setLoadFailed] = useState(false);
  const [saved, setSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string>();
  const payment = useRef<PaymentHandle | null>(null);

  useEffect(() => {
    if (!token) return;
    PublicFormsService.loadPublicForm({ requestBody: { token } })
      .then(setView)
      .catch(() => setLoadFailed(true));
  }, [token]);

  // Bring the first marked answer into view after a refused submit.
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      document
        .querySelector("[data-form-error]")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errors]);

  /** Stripe's page for the tutor's payroll bank, in a new tab so the form stays. */
  function addPayrollBank() {
    if (!token) return;
    const tab = window.open("", "_blank"); // opened on the click, so it is not blocked
    PublicFormsService.startPublicFormPayrollLink({ requestBody: { token } })
      .then(({ url }) => {
        if (tab) tab.location.href = url;
        else window.location.href = url;
      })
      .catch((e) => {
        tab?.close();
        setErrors({
          payroll: errorText(e, "Stripe could not be opened. Try again."),
        });
      });
  }

  async function submit(answers: PublicFormAnswers) {
    if (!token) return;
    setSubmitting(true);
    setNotice(undefined);
    // Save the payment method with Stripe first; the server checks it.
    let setupIntentId: string | null = null;
    if (view?.payment && payment.current) {
      // Never save a card for a submit the server will refuse for the terms.
      if (view.terms_text != null && !answers.terms_agreed) {
        setErrors({ terms: "Tick the box to agree to the terms." });
        setSubmitting(false);
        return;
      }
      try {
        const result = await payment.current.confirm();
        if (result.error) {
          setErrors({ payment: result.error });
          setSubmitting(false);
          return;
        }
        setupIntentId = result.setupIntentId;
      } catch {
        setErrors({
          payment:
            "Your payment method could not be saved. Check your connection and try again.",
        });
        setSubmitting(false);
        return;
      }
    }
    PublicFormsService.submitPublicForm({
      requestBody: { ...answers, token, setup_intent_id: setupIntentId },
    })
      .then((result) => {
        if (result.ok) {
          setSaved(true);
          window.scrollTo(0, 0);
          return;
        }
        if (result.state === "errors") {
          setErrors(result.errors || {});
        } else if (result.state === "form_updated" && result.form) {
          setErrors({});
          setView(result.form);
          setNotice(
            "This form was just updated. Please check your answers and submit again."
          );
          window.scrollTo(0, 0);
        } else if (result.state === "done") {
          setView({
            ...(view as PublicFormView),
            state: PublicFormView.state.DONE,
          });
        } else {
          setView({
            state: PublicFormView.state.INVALID,
            org_name: view?.org_name,
          });
        }
      })
      .catch(() => {
        // The notice is at the top of the form; bring it into view.
        setNotice("Could not save. Check your connection and try again.");
        window.scrollTo(0, 0);
      })
      .finally(() => setSubmitting(false));
  }

  if (loadFailed) {
    return (
      <Message
        title="This form could not be opened."
        text="Check your connection and reload the page."
      />
    );
  }
  if (!view) {
    return (
      <Card>
        <div className="animate-pulse space-y-4 py-4">
          <div className="h-4 w-1/3 rounded bg-gray-200" />
          <div className="h-7 w-2/3 rounded bg-gray-200" />
          <div className="h-11 rounded bg-gray-100" />
          <div className="h-11 rounded bg-gray-100" />
        </div>
      </Card>
    );
  }
  if (saved) {
    return (
      <Message
        done
        title={
          view.recipient_first_name
            ? `Thank you, ${view.recipient_first_name}!`
            : "Thank you!"
        }
        text="Your answers were saved. You can close this page."
      />
    );
  }
  if (view.state === PublicFormView.state.DONE) {
    return (
      <Message
        done
        title="This form is already filled out."
        text="Thank you! You can close this page."
      />
    );
  }
  if (view.state === PublicFormView.state.INVALID) {
    return (
      <Message
        title="This link is no longer active."
        text={
          view.org_name
            ? `Ask ${view.org_name} to send you a new one.`
            : "Ask whoever sent it to send you a new one."
        }
      />
    );
  }

  return (
    <Card>
      <PublicForm
        view={view}
        submitting={submitting}
        errors={errors}
        notice={notice}
        onSubmit={submit}
        onAddPayrollBank={addPayrollBank}
        paymentSlot={
          view.payment && token ? (
            <PaymentStep
              token={token}
              required={view.payment.required}
              handle={payment}
              error={errors["payment"]}
              disabled={submitting}
            />
          ) : undefined
        }
      />
    </Card>
  );
}
